import React, {
  useEffect, useMemo, useState, useRef, useLayoutEffect,
} from "react"
import Quickfilter from "~components/Quickfilter"
import SidebarTag from "./SidebarTag"
import SidebarResources from "./SidebarResources"
import SidebarDeprecatedTags from "./SidebarDeprecatedTags"
import {
  isTagNotEmpty, stripText, filterByTextAndGroupByTagAndVersion, isResourcePath,
} from "./utils"
import { mapAllResources, mapTags } from '../../../utils/versioning'

import {
  sidebar, sidebarHeader, sidebarBody, itemsCategory,
} from "./Sidebar.module.scss"

const Sidebar = ({
  className, microcopy, tags, deprecatedTags, resources, location,
}) => {
  const sidebarResources = useMemo(() => mapAllResources(resources), [resources])

  const mappedTags = useMemo(() => mapTags(tags), [tags])

  const allEndpoints = useMemo(
    () => [tags, deprecatedTags].flat().filter((tag) => tag.endpoints?.[0])
      .map((tag) => tag.endpoints.map((endpoint) => ({ tag, item: endpoint }))).flat(),
    [tags, deprecatedTags],
  )

  const allResources = useMemo(
    () => [tags, deprecatedTags].flat().filter((tag) => tag.resources?.[0])
      .map((tag) => tag.resources.map((resource) => ({ tag, item: resource }))).flat(),
    [tags, deprecatedTags],
  )

  const [filterText, setFilterText] = useState('')
  const sidebarBodyRef = useRef(null)
  const [bodyHeight, setBodyHeight] = useState(0)

  useEffect(() => {
    if (sidebarBodyRef.current && !!stripText(filterText)) {
      sidebarBodyRef.current.scrollTop = 0
    }
  }, [filterText])

  useLayoutEffect(() => {
    const { height } = sidebarBodyRef.current.getBoundingClientRect()
    setBodyHeight(height)
  }, [])

  const filteredItems = useMemo(() => {
    const filter = stripText(filterText)
    if (!filter) {
      return []
    }
    let result = {}
    filterByTextAndGroupByTagAndVersion(allEndpoints, filter, 'endpoints', result)
    filterByTextAndGroupByTagAndVersion(allResources, filter, 'resources', result)
    return Object.values(result)
  }, [filterText, allEndpoints, allResources])

  const filteredResources = useMemo(
    () => {
      const filter = stripText(filterText)
      if (!filter) {
        return []
      }
      return sidebarResources.filter((resource) => stripText(resource.title).includes(filter))
    },
    [filteredItems],
  )

  const isTagResourceSelected = isResourcePath(location.pathname)
    && tags.map((tag) => tag.resources).flat().map((resource) => resource.path).includes(location.pathname)

  return (
    <aside id="sidebar" className={className}>
      <div className={sidebar}>
        <div className={sidebarHeader}>
          <h1>{microcopy.text("reference.titles.api_reference")}</h1>
          <Quickfilter filterValue={filterText} onValueChange={(value) => setFilterText(value)} microcopy={microcopy} />
        </div>
        <div ref={sidebarBodyRef} className={sidebarBody}>
          {!stripText(filterText) ? (
            <ul>
              {mappedTags.filter(isTagNotEmpty).map((tag) => (
                <li tabIndex={0} key={tag.id}>
                  <SidebarTag
                    tag={tag}
                    endpointsByVersion={{ [tag.version]: tag.endpoints }}
                    resourcesByVersion={{ [tag.version]: tag.resources }}
                    location={location}
                    bodyHeight={bodyHeight}
                  />
                </li>
              ))}
              {deprecatedTags?.some(isTagNotEmpty) && (
                <li tabIndex={0} className={itemsCategory} key="deprecatedTags">
                  <SidebarDeprecatedTags
                    tags={deprecatedTags}
                    microcopy={microcopy}
                    location={location}
                    bodyHeight={bodyHeight}
                  />
                </li>
              )}
              <li tabIndex={0} className={itemsCategory} key="allResources">
                <SidebarResources
                  resources={sidebarResources}
                  highlightSelected={!isTagResourceSelected}
                  microcopy={microcopy}
                  location={location}
                />
              </li>
            </ul>
          ) : (
            <ul>
              {filteredItems.map((tag) => (
                <li tabIndex={0} key={tag.tag.key}>
                  <SidebarTag
                    tag={tag.tag}
                    endpointsByVersion={tag.endpoints}
                    resourcesByVersion={tag.resources}
                    versions={tag.versions}
                    highlightSelected={false}
                    itemsWithVersion
                    location={location}
                    open
                  />
                </li>
              ))}
              {filteredResources && (
                <li tabIndex={0} key="filteredResources">
                  <SidebarResources
                    resources={filteredResources}
                    open
                    microcopy={microcopy}
                    location={location}
                    highlightSelected={false}
                  />
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </aside>
  )
}

export default Sidebar
